import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";
import ModeToggle from "../components/ModeToggle";
import VehicleCard from "../components/VehicleCard";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "flagship-suv", label: "Flagship SUV" },
  { value: "flagship-sedan", label: "Flagship Sedan" },
  { value: "performance-sedan", label: "Performance Sedan" },
  { value: "luxury-suv", label: "Luxury SUV" },
  { value: "super-suv", label: "Super SUV" },
  { value: "executive-sedan", label: "Executive Sedan" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "compact-suv", label: "Compact SUV" },
  { value: "van", label: "Sprinter / Van" },
];

export default function Fleet() {
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);

  const mode = params.get("mode") || "all";
  const category = params.get("category") || "";
  const capacity = params.get("capacity") || "";
  const start = params.get("start") || "";
  const end = params.get("end") || "";

  const setMode = (m) => {
    const next = new URLSearchParams(params);
    next.set("mode", m);
    setParams(next);
  };
  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set("category", c); else next.delete("category");
    setParams(next);
  };
  const setCapacity = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set("capacity", c); else next.delete("capacity");
    setParams(next);
  };

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (mode !== "all") q.available_for = mode;
    if (category) q.category = category;
    if (capacity) q.min_capacity = capacity;
    api.get("/vehicles", { params: q })
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  }, [mode, category, capacity]);

  // Load live specials banners once — best-effort, don't block the page.
  useEffect(() => {
    api.get("/specials")
      .then((r) => setSpecials(Array.isArray(r.data) ? r.data.filter((s) => s.is_live) : []))
      .catch(() => setSpecials([]));
  }, []);

  const heading = useMemo(() => ({
    all: "The complete fleet",
    chauffeur: "Chauffeured luxury",
    rental: "Self-drive rentals",
  }[mode]), [mode]);

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      {specials.length > 0 && (
        <div data-testid="fleet-specials-banner" className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {specials.slice(0, 3).map((s) => (
            <Link
              key={s.id}
              to={`/promo/${s.promo_code}`}
              className="glass rounded-2xl border border-cyan/30 hover:border-cyan/60 p-4 flex items-center gap-3 group transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-cyan/15 text-cyan flex-shrink-0 flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-caps text-cyan text-[10px]">Special</div>
                <div className="text-sm text-white leading-tight truncate">{s.title}</div>
                <div className="text-[11px] text-white/60">
                  ${s.hourly_rate}/hr · {s.min_hours}-hr min
                </div>
              </div>
              <ArrowUpRight size={16} className="text-cyan flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ))}
        </div>
      )}
      {start && end && (
        <div
          data-testid="fleet-date-banner"
          className="mb-6 rounded-2xl border border-cream/30 bg-cream/[0.05] px-4 py-3 text-sm text-cream flex items-center gap-3 flex-wrap"
        >
          <span className="label-caps text-[10px]">Searching for</span>
          <span className="text-white">{new Date(start).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          <span className="text-white/50">→</span>
          <span className="text-white">{new Date(end).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          <button
            type="button"
            onClick={() => { const n = new URLSearchParams(params); n.delete("start"); n.delete("end"); setParams(n); }}
            className="ml-auto text-xs text-white/60 hover:text-white"
          >
            Clear dates
          </button>
        </div>
      )}
      <div className="flex flex-col gap-6 mb-10">
        <div>
          <div className="label-caps text-gold mb-2">Fleet</div>
          <h1 className="font-serif text-4xl sm:text-5xl">{heading}</h1>
          <p className="text-white/60 mt-3 max-w-2xl">
            {mode === "rental"
              ? "All rentals require a two-day minimum booking and a valid driver’s license. Kia K4 & Sportage are approved for Uber, Lyft, Uber Eats and DoorDash."
              : mode === "chauffeur"
              ? "Discreet, licensed chauffeurs. Airport transfers, weddings, executive roadshows and beyond."
              : "Browse everything — book to be driven or drive it yourself."}
          </p>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <ModeToggle mode={mode === "all" ? "chauffeur" : mode} onChange={setMode} size="lg" />
          <button
            onClick={() => setMode("all")}
            className={`px-4 py-2 rounded-full text-xs label-caps transition-colors ${
              mode === "all" ? "bg-gold text-ink-900" : "text-white/60 border border-white/10 hover:text-white"
            }`}
          >
            Everything
          </button>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            data-testid={TID.fleet.filterCategory}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-sm focus:border-gold focus:outline-none"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            data-testid={TID.fleet.filterCapacity}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            className="bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-sm focus:border-gold focus:outline-none"
          >
            <option value="">Any capacity</option>
            <option value="2">2+ seats</option>
            <option value="4">4+ seats</option>
            <option value="6">6+ seats</option>
            <option value="8">8+ seats</option>
            <option value="10">10+ seats</option>
            <option value="14">14+ seats</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((k) => (
            <div key={k} className="aspect-[16/10] rounded-2xl bg-ink-800 animate-pulse" />
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center">
          <p className="text-white/70">No vehicles match those filters.</p>
        </div>
      ) : (
        <div
          data-testid={TID.fleet.grid}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {vehicles.map((v) => (
            <VehicleCard key={v.id} vehicle={v} searchStart={start} searchEnd={end} />
          ))}
        </div>
      )}
    </section>
  );
}
